import katex from 'katex';
export const isSafeUrl = (url) =>
  typeof url === 'string' &&
  /^(https?:\/\/|mailto:|tel:|\/(?!\/)|#)/i.test(url.trim()) &&
  !/[\u0000-\u0020]/.test(url.trim());
const escape = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (ch) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[
        ch
      ],
  );
export class ImageOptimizeError extends Error {}
export const ACCEPTED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
];
export const extensionForMime = (mime) =>
  ({
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
  })[mime] || 'png';
export async function optimizeImageFile(file) {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type))
    throw new ImageOptimizeError('Choose a PNG, JPEG, WebP, or GIF image.');
  if (file.size > 10 * 1024 * 1024)
    throw new ImageOptimizeError('Images must be 10 MB or smaller.');
  // Preserve animation and transparency; no lossy re-encoding.
  return { blob: file, mimeType: file.type };
}
export async function tryUploadQuestionMedia(file, name, uploadFile) {
  try {
    if (uploadFile) {
      const result = await uploadFile(file, name);
      if (!result?.url || !isSafeUrl(result.url))
        throw new Error(
          'uploadFile must return a safe HTTP(S) or relative URL.',
        );
      return { url: result.url, assetId: result.assetId || '' };
    }
    const url = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => reject(new Error('Could not read this file.'));
      r.readAsDataURL(file);
    });
    return { url, assetId: '' };
  } catch (e) {
    return { error: e.message || 'Upload failed.' };
  }
}
export const ATTACHMENT_REMOVE_ATTR = 'data-rte-attachment-remove';
export const formatAttachmentSize = (bytes) =>
  bytes < 1024
    ? `${bytes} B`
    : bytes < 1048576
      ? `${(bytes / 1024).toFixed(1)} KB`
      : `${(bytes / 1048576).toFixed(1)} MB`;
const wrap = (name, html) =>
  `<figure data-attachment="true" data-name="${escape(name)}" contenteditable="false" style="position:relative;padding:12px;border:1px solid #d1d5db;border-radius:8px;background:#f8fafc;color:#0f172a">${html}</figure>`;
export const buildFileAttachmentHtml = (name, size, url) =>
  wrap(
    name,
    `<a href="${escape(url)}" download="${escape(name)}" target="_blank" rel="noopener noreferrer">📎 ${escape(name)}</a><small style="display:block">${formatAttachmentSize(size)}</small>`,
  );
export const buildImageAttachmentHtml = (name, url) =>
  wrap(
    name,
    `<img src="${escape(url)}" alt="${escape(name)}" style="max-width:100%;height:auto"/><figcaption>${escape(name)}</figcaption>`,
  );
export const buildMediaAttachmentHtml = (type, name, url) =>
  wrap(
    name,
    `<${type} controls src="${escape(url)}" style="max-width:100%"></${type}><figcaption>${escape(name)}</figcaption>`,
  );
export const buildPdfAttachmentHtml = buildFileAttachmentHtml;
export const lockAttachmentNodes = (root) =>
  root
    .querySelectorAll('[data-attachment]')
    .forEach((el) => el.setAttribute('contenteditable', 'false'));
export const stripAttachmentRemoveButtons = (root) =>
  root
    .querySelectorAll(`[${ATTACHMENT_REMOVE_ATTR}]`)
    .forEach((el) => el.remove());
export const decorateAttachmentRemoveButtons = (root) =>
  root.querySelectorAll('[data-attachment]').forEach((el) => {
    if (el.querySelector(`[${ATTACHMENT_REMOVE_ATTR}]`)) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute(ATTACHMENT_REMOVE_ATTR, 'true');
    button.setAttribute(
      'aria-label',
      `Remove ${el.dataset.name || 'attachment'}`,
    );
    button.className = 'rte-attachment-remove';
    button.textContent = '×';
    el.append(button);
  });
export const ensureEditableTailAfterAttachments = (root) => {
  if (root.lastElementChild?.matches('[data-attachment]')) {
    const p = document.createElement('p');
    p.innerHTML = '<br>';
    root.append(p);
  }
};
export const upgradeLegacyFileAttachmentLinks = () => {};
export const renderMathElements = (root) =>
  root.querySelectorAll('.math-equation[data-latex]').forEach((el) => {
    if (el.dataset.renderedLatex === el.dataset.latex) return;
    katex.render(el.dataset.latex, el, {
      throwOnError: false,
      displayMode: false,
      trust: false,
    });
    el.dataset.renderedLatex = el.dataset.latex;
  });

export function normalizeLinkUrl(value) {
  const text = String(value || '').trim();
  if (!text) return null;
  if (/^(mailto:|tel:|#|\/(?!\/))/i.test(text))
    return isSafeUrl(text) ? text : null;
  if (/^[a-z][a-z\d+.-]*:/i.test(text) && !/^https?:/i.test(text)) return null;
  const candidate = /^https?:\/\//i.test(text) ? text : `https://${text}`;
  try {
    const url = new URL(candidate);
    if (
      !['http:', 'https:'].includes(url.protocol) ||
      !url.hostname ||
      /\s/.test(url.hostname)
    )
      return null;
    if (
      !/^https?:\/\//i.test(text) &&
      !url.hostname.includes('.') &&
      url.hostname !== 'localhost'
    )
      return null;
    return url.href;
  } catch {
    return null;
  }
}
